import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Text, Table, Thead, Tbody, Tr, Th, Td,
  Input, Select, FormControl, FormLabel, HStack, VStack, useToast, Flex,
  Badge, IconButton, Spinner, InputGroup, InputLeftElement, Icon, Tooltip,
  Switch, Alert, AlertIcon, NumberInput, NumberInputField, Textarea,
  Tag, TagLabel, Button, Divider,
} from '@chakra-ui/react';
import {
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiSave, FiX, FiUpload, FiPackage,
  FiCheckCircle, FiTrendingUp, FiTrendingDown, FiRotateCcw, FiAlertTriangle,
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import PageShell from './PageShell';
import api from '../api';

const CATEGORIES = [
  { value: 'income',      label: 'Income',      color: 'green'  },
  { value: 'expenditure', label: 'Expenditure', color: 'red'    },
  { value: 'assets',      label: 'Assets',      color: 'blue'   },
  { value: 'liabilities', label: 'Liabilities', color: 'orange' },
  { value: 'equity',      label: 'Equity',      color: 'purple' },
  { value: 'other',       label: 'Other',       color: 'gray'   },
];

const sageCategoryFromCode = (codeStr) => {
  const n = parseInt(codeStr);
  if (isNaN(n)) return 'other';
  if (n < 2000) return 'assets';
  if (n < 3000) return 'liabilities';
  if (n < 4000) return 'equity';
  if (n < 5000) return 'income';
  if (n < 8000) return 'expenditure';
  return 'other';
};

const catColor = (c) => CATEGORIES.find(x => x.value === c)?.color || 'gray';
const catLabel = (c) => CATEGORIES.find(x => x.value === c)?.label || c;

const BLANK = {
  code: '', name: '', category: 'income', description: '',
  debit_balance: '', credit_balance: '',
  notes: '', is_obsolete: false, replacement_code_id: '',
};

const fmtBalance = (v) => {
  const n = parseFloat(v || 0);
  if (n === 0) return null;
  return '£' + Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const normaliseRow = (row) => {
  const r = {};
  Object.keys(row).forEach(k => { r[k.toLowerCase().replace(/[\s\/]/g, '_')] = row[k]; });
  const code = String(r['n_c'] || r['n/c'] || r['nominal_code'] || r['code'] || r['account_code'] || r['a_c'] || r['a/c'] || '').trim().toUpperCase();
  const name = String(r['name'] || r['account_name'] || r['nominal_name'] || r['description'] || '').trim();
  if (!code || !name || isNaN(parseInt(code))) return null;
  const parseNum = (v) => { if (!v && v !== 0) return 0; const n = parseFloat(String(v).replace(/[£,\s]/g, '')); return isNaN(n) ? 0 : n; };
  return {
    code, name,
    debit_balance:  parseNum(r['debit']  || r['debit_balance']  || r['dr'] || r['debit_value']  || 0),
    credit_balance: parseNum(r['credit'] || r['credit_balance'] || r['cr'] || r['credit_value'] || 0),
  };
};

const parseAny = (input, isBuffer) => {
  const wb = XLSX.read(input, { type: isBuffer ? 'array' : 'string' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(ws, { defval: '' }).map(normaliseRow).filter(Boolean);
};

export default function NominalCodesModal({ isOpen, onClose, fullPage, onBack }) {
  const active = fullPage || isOpen;
  const [codes, setCodes]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [query, setQuery]               = useState('');
  const [filterCat, setFilterCat]       = useState('');
  const [filterProducts, setFilterProducts]   = useState(false);
  const [showObsolete, setShowObsolete]       = useState(false);
  const [showDeleted, setShowDeleted]         = useState(false);
  const [showForm, setShowForm]         = useState(false);
  const [showImport, setShowImport]     = useState(false);
  const [editId, setEditId]             = useState(null);
  const [form, setForm]                 = useState(BLANK);
  const [codeError, setCodeError]       = useState(false);
  const [saving, setSaving]             = useState(false);
  const [importRows, setImportRows]     = useState([]);
  const [importing, setImporting]       = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef();
  const toast = useToast();

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterProducts) params.set('used_in_products', 'true');
      if (showObsolete)   params.set('include_obsolete', 'true');
      if (showDeleted)    params.set('include_deleted',  'true');
      const { data } = await api.get(`/nominal-codes?${params}`);
      setCodes(data);
    } catch(e) {
      toast({ title: 'Failed to load nominal codes: ' + (e.response?.data?.error || e.message), status: 'error', duration: 5000 });
    }
    setLoading(false);
  }, [filterProducts, showObsolete, showDeleted, toast]);

  useEffect(() => { if (active) fetchCodes(); }, [active, fetchCodes]);

  const set    = (k) => (e)   => { if (k === 'code') setCodeError(false); setForm(p => ({ ...p, [k]: e.target.value })); };
  const setNum = (k) => (val) => setForm(p => ({ ...p, [k]: val }));
  const setBool = (k) => (e)  => setForm(p => ({ ...p, [k]: e.target.checked }));

  const openEdit = (nc) => {
    setEditId(nc.id); setCodeError(false);
    setForm({
      code: nc.code, name: nc.name, category: nc.category,
      description: nc.description || '',
      debit_balance:       nc.debit_balance  != null ? String(nc.debit_balance)  : '',
      credit_balance:      nc.credit_balance != null ? String(nc.credit_balance) : '',
      notes:               nc.notes || '',
      is_obsolete:         !!nc.is_obsolete,
      replacement_code_id: nc.replacement_code_id || '',
    });
    setShowForm(true); setShowImport(false);
    setTimeout(() => document.getElementById('nc-form-top')?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  const resetForm = () => { setForm(BLANK); setEditId(null); setCodeError(false); setShowForm(false); };

  const save = async () => {
    if (!form.code || !form.name) { toast({ title: 'Code and name required', status: 'warning', duration: 2000 }); return; }
    const dup = codes.find(c => c.code.toUpperCase() === form.code.toUpperCase() && c.id !== editId && !c.deleted_at);
    if (dup) { setCodeError(true); toast({ title: 'That nominal code is already in use', status: 'error', duration: 4000 }); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        debit_balance:       parseFloat(form.debit_balance)  || 0,
        credit_balance:      parseFloat(form.credit_balance) || 0,
        replacement_code_id: form.replacement_code_id || null,
      };
      if (editId) await api.put(`/nominal-codes/${editId}`, payload);
      else        await api.post('/nominal-codes', payload);
      toast({ title: 'Saved', status: 'success', duration: 1500 });
      resetForm(); fetchCodes();
    } catch(e) {
      const msg = e.response?.data?.error || 'Failed';
      if (msg.includes('already in use')) setCodeError(true);
      toast({ title: msg, status: 'error', duration: 3000 });
    }
    setSaving(false);
  };

  const deleteCode   = async (id) => { await api.delete(`/nominal-codes/${id}`); fetchCodes(); };
  const restoreCode  = async (id) => {
    try { await api.patch(`/nominal-codes/${id}/restore`); toast({ title: 'Code restored', status: 'success', duration: 1500 }); fetchCodes(); }
    catch(e) { toast({ title: 'Restore failed', status: 'error', duration: 2000 }); }
  };
  const toggleObsolete = async (nc) => {
    try { await api.put(`/nominal-codes/${nc.id}`, { is_obsolete: !nc.is_obsolete }); fetchCodes(); }
    catch(e) { toast({ title: 'Failed to update', status: 'error', duration: 2000 }); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportResult(null);
    const isCSV = /\.(csv|txt)$/i.test(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const rows = parseAny(isCSV ? ev.target.result : new Uint8Array(ev.target.result), !isCSV);
        if (!rows.length) toast({ title: 'No valid rows found — ensure file has code and name columns', status: 'warning', duration: 4000 });
        setImportRows(rows);
      } catch(err) { toast({ title: 'Could not parse file: ' + err.message, status: 'error', duration: 4000 }); }
    };
    if (isCSV) reader.readAsText(file); else reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleImport = async () => {
    if (!importRows.length) return;
    setImporting(true);
    try {
      const { data } = await api.post('/nominal-codes/bulk-import', { codes: importRows });
      setImportResult(data);
      if (data.errors > 0 && data.inserted === 0) {
        toast({ title: `Import failed — ${data.errors} errors, 0 inserted. Check server logs.`, status: 'error', duration: 5000 });
      } else {
        toast({ title: `Import complete — ${data.inserted} inserted, ${data.errors} errors`, status: data.errors > 0 ? 'warning' : 'success', duration: 3000 });
      }
      fetchCodes(); setImportRows([]);
    } catch(e) { toast({ title: e.response?.data?.error || 'Import failed', status: 'error', duration: 3000 }); }
    setImporting(false);
  };

  const filtered = codes.filter(c => {
    const q = query.toLowerCase();
    const matchQ = !q || c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q) || (c.description||'').toLowerCase().includes(q) || (c.notes||'').toLowerCase().includes(q);
    return matchQ && (!filterCat || c.category === filterCat);
  });

  const grouped = CATEGORIES.map(cat => ({
    ...cat,
    items:       filtered.filter(c => c.category === cat.value),
    totalDebit:  filtered.filter(c => c.category === cat.value).reduce((s, c) => s + parseFloat(c.debit_balance  || 0), 0),
    totalCredit: filtered.filter(c => c.category === cat.value).reduce((s, c) => s + parseFloat(c.credit_balance || 0), 0),
  })).filter(g => g.items.length > 0);

  const obsoleteCount = codes.filter(c => c.is_obsolete  && !c.deleted_at).length;
  const deletedCount  = codes.filter(c => c.deleted_at).length;
  const hasBalances   = importRows.some(r => r.debit_balance !== 0 || r.credit_balance !== 0);
  const replacementOptions = codes.filter(c => !c.deleted_at && !c.is_obsolete);

  const headerRight = (
    <HStack spacing={2}>
      <Button size="sm" leftIcon={<FiUpload size={13} />} colorScheme="teal" variant={showImport ? 'solid' : 'outline'}
        onClick={() => { setShowImport(p => !p); setShowForm(false); resetForm(); }}>
        {showImport ? 'Cancel Import' : 'Import from Sage'}
      </Button>
      <Button size="sm" leftIcon={showForm ? <FiX size={13} /> : <FiPlus size={13} />} colorScheme="blue" variant={showForm ? 'solid' : 'outline'}
        onClick={() => { if (showForm) resetForm(); else { setEditId(null); setForm(BLANK); setShowForm(true); setShowImport(false); } }}>
        {showForm ? 'Cancel' : 'Add Code'}
      </Button>
    </HStack>
  );

  return (
    <PageShell fullPage={fullPage} onBack={onBack} isOpen={isOpen} onClose={onClose}
      title={`Nominal Codes${codes.length ? ` (${codes.length})` : ''}`}
      headerRight={headerRight}>

      {/* ── Centred content wrapper ── */}
      <Box maxW="1200px" mx="auto" py={4} px={4}>

        {/* ── Import panel ── */}
        {showImport && (
          <Box bg="teal.50" p={5} borderRadius="xl" mb={6} border="1px solid" borderColor="teal.200"
            boxShadow="sm">
            <HStack mb={1} justify="space-between">
              <Text fontSize="14px" fontWeight="700" color="teal.700">Import Nominal Codes</Text>
              <Badge colorScheme="teal" fontSize="10px">CSV · XLSX · XLS · ODS</Badge>
            </HStack>
            <Text fontSize="12px" color="teal.600" mb={4}>
              Export your nominal codes from Sage (Nominal Codes list → Export). Required columns: <strong>N/C</strong> and <strong>Name</strong>.
              Debit/Credit balance columns are imported automatically. Categories are auto-assigned from Sage code ranges.
              Existing codes are skipped; non-zero balances from the file update stored values.
            </Text>

            {importResult && (
              <Alert status={importResult.errors > 0 && importResult.inserted === 0 ? 'error' : importResult.errors > 0 ? 'warning' : 'success'} borderRadius="lg" mb={4} py={2}>
                <AlertIcon boxSize={4} />
                <Text fontSize="12px">
                  <strong>{importResult.inserted}</strong> codes inserted from {importResult.total} rows.
                  {importResult.skipped > 0 && <Text as="span" color="gray.500"> {importResult.skipped} skipped (blank).</Text>}
                  {importResult.errors  > 0 && <Text as="span" color="red.600"> {importResult.errors} errors.</Text>}
                </Text>
              </Alert>
            )}

            <input type="file" accept=".csv,.txt,.xlsx,.xls,.ods" ref={fileRef} onChange={handleFileSelect} style={{ display: 'none' }} />

            {importRows.length === 0 ? (
              <Box
                border="2px dashed" borderColor="teal.300" borderRadius="xl"
                p={8} textAlign="center" cursor="pointer"
                _hover={{ borderColor: 'teal.500', bg: 'teal.100' }}
                transition="all 0.15s"
                onClick={() => fileRef.current?.click()}>
                <Icon as={FiUpload} boxSize={8} color="teal.400" mb={2} />
                <Text fontSize="14px" fontWeight="600" color="teal.700">Click to choose your file</Text>
                <Text fontSize="11px" color="teal.500" mt={1}>Supports CSV, Excel (.xlsx, .xls) and ODS</Text>
              </Box>
            ) : (
              <Box>
                <HStack mb={3} justify="space-between" align="center">
                  <HStack>
                    <Icon as={FiCheckCircle} color="teal.500" />
                    <Text fontSize="13px" fontWeight="600" color="teal.700">{importRows.length} codes ready to import</Text>
                  </HStack>
                  <Button size="xs" variant="ghost" colorScheme="gray" onClick={() => setImportRows([])}>Clear</Button>
                </HStack>
                <Box border="1px" borderColor="teal.200" borderRadius="lg" overflow="hidden" mb={4}>
                  <Table size="sm">
                    <Thead bg="teal.100">
                      <Tr>
                        <Th fontSize="10px" px={3}>Code</Th>
                        <Th fontSize="10px" px={3}>Name</Th>
                        <Th fontSize="10px" px={3}>Auto Category</Th>
                        {hasBalances && <><Th fontSize="10px" px={3} textAlign="right" color="blue.700">Debit</Th><Th fontSize="10px" px={3} textAlign="right" color="red.700">Credit</Th></>}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {importRows.slice(0, 10).map((r, i) => (
                        <Tr key={i}>
                          <Td px={3} fontSize="12px" fontFamily="mono" fontWeight="600">{r.code}</Td>
                          <Td px={3} fontSize="12px">{r.name}</Td>
                          <Td px={3}><Badge colorScheme={catColor(sageCategoryFromCode(r.code))} fontSize="9px">{catLabel(sageCategoryFromCode(r.code))}</Badge></Td>
                          {hasBalances && <>
                            <Td px={3} fontSize="11px" textAlign="right" color="blue.700">{r.debit_balance  ? fmtBalance(r.debit_balance)  : '—'}</Td>
                            <Td px={3} fontSize="11px" textAlign="right" color="red.700"> {r.credit_balance ? fmtBalance(r.credit_balance) : '—'}</Td>
                          </>}
                        </Tr>
                      ))}
                      {importRows.length > 10 && (
                        <Tr><Td colSpan={hasBalances ? 5 : 3} px={3} fontSize="11px" color="gray.400" py={2} textAlign="center">
                          … and {importRows.length - 10} more codes
                        </Td></Tr>
                      )}
                    </Tbody>
                  </Table>
                </Box>
                <Flex justify="space-between" align="center">
                  <Button size="sm" variant="outline" leftIcon={<FiUpload size={12} />} onClick={() => fileRef.current?.click()}>
                    Choose different file
                  </Button>
                  <Button colorScheme="teal" leftIcon={<FiCheckCircle size={14} />} onClick={handleImport} isLoading={importing} loadingText="Importing…">
                    Import {importRows.length} Codes
                  </Button>
                </Flex>
              </Box>
            )}
          </Box>
        )}

        {/* ── Add / Edit form ── */}
        {showForm && (
          <Box id="nc-form-top" bg="blue.50" p={5} borderRadius="xl" mb={6} border="1px solid" borderColor="blue.200" boxShadow="sm">
            <Text fontSize="14px" fontWeight="700" color="blue.700" mb={4}>{editId ? 'Edit Nominal Code' : 'New Nominal Code'}</Text>
            <VStack spacing={4} align="stretch">
              <HStack spacing={4}>
                <FormControl isRequired maxW="130px">
                  <FormLabel fontSize="11px" mb={1} color={codeError ? 'red.500' : 'gray.600'}>
                    Code {codeError && <Text as="span" fontSize="9px" color="red.500">(already in use)</Text>}
                  </FormLabel>
                  <Input bg="white" size="sm" value={form.code} onChange={set('code')} textTransform="uppercase"
                    placeholder="e.g. 4000" borderColor={codeError ? 'red.400' : undefined} />
                </FormControl>
                <FormControl isRequired flex={2}>
                  <FormLabel fontSize="11px" mb={1} color="gray.600">Name</FormLabel>
                  <Input bg="white" size="sm" value={form.name} onChange={set('name')} placeholder="e.g. Sales Revenue" />
                </FormControl>
                <FormControl maxW="160px">
                  <FormLabel fontSize="11px" mb={1} color="gray.600">Category</FormLabel>
                  <Select bg="white" size="sm" value={form.category} onChange={set('category')}>
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </Select>
                </FormControl>
              </HStack>

              <HStack spacing={4}>
                <FormControl flex={2}>
                  <FormLabel fontSize="11px" mb={1} color="gray.600">Description</FormLabel>
                  <Input bg="white" size="sm" value={form.description} onChange={set('description')} placeholder="Brief description…" />
                </FormControl>
                <FormControl maxW="140px">
                  <FormLabel fontSize="11px" mb={1}>
                    <HStack spacing={1}><Icon as={FiTrendingUp} boxSize={3} color="blue.500" /><Text color="blue.700">Debit (£)</Text></HStack>
                  </FormLabel>
                  <NumberInput bg="white" size="sm" value={form.debit_balance} onChange={setNum('debit_balance')} min={0} precision={2}>
                    <NumberInputField placeholder="0.00" />
                  </NumberInput>
                </FormControl>
                <FormControl maxW="140px">
                  <FormLabel fontSize="11px" mb={1}>
                    <HStack spacing={1}><Icon as={FiTrendingDown} boxSize={3} color="red.500" /><Text color="red.600">Credit (£)</Text></HStack>
                  </FormLabel>
                  <NumberInput bg="white" size="sm" value={form.credit_balance} onChange={setNum('credit_balance')} min={0} precision={2}>
                    <NumberInputField placeholder="0.00" />
                  </NumberInput>
                </FormControl>
              </HStack>

              <HStack spacing={4} align="flex-start">
                <FormControl maxW="160px">
                  <FormLabel fontSize="11px" mb={1} color="orange.600">
                    <HStack spacing={1}><Icon as={FiAlertTriangle} boxSize={3} /><Text>Obsolete</Text></HStack>
                  </FormLabel>
                  <HStack spacing={2} mt={2}>
                    <Switch size="sm" isChecked={form.is_obsolete} colorScheme="orange" onChange={setBool('is_obsolete')} />
                    <Text fontSize="12px" color={form.is_obsolete ? 'orange.600' : 'gray.400'}>
                      {form.is_obsolete ? 'Yes' : 'No'}
                    </Text>
                  </HStack>
                </FormControl>
                <FormControl flex={1} opacity={form.is_obsolete ? 1 : 0.4}>
                  <FormLabel fontSize="11px" mb={1} color="gray.600">Replacement Code</FormLabel>
                  <Select bg="white" size="sm" value={form.replacement_code_id} onChange={set('replacement_code_id')}
                    placeholder="— none —" isDisabled={!form.is_obsolete}>
                    {replacementOptions.filter(c => !editId || c.id !== editId)
                      .sort((a,b) => a.code.localeCompare(b.code))
                      .map(c => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
                  </Select>
                </FormControl>
                <FormControl flex={2}>
                  <FormLabel fontSize="11px" mb={1} color="gray.600">Notes</FormLabel>
                  <Textarea bg="white" size="sm" value={form.notes} onChange={set('notes')} placeholder="Internal notes…" rows={2} resize="none" />
                </FormControl>
              </HStack>

              <Flex justify="flex-end" gap={2}>
                <Button size="sm" variant="ghost" onClick={resetForm}>Cancel</Button>
                <Button size="sm" colorScheme="blue" leftIcon={<FiSave size={13} />} onClick={save} isLoading={saving}>
                  {editId ? 'Update Code' : 'Add Code'}
                </Button>
              </Flex>
            </VStack>
          </Box>
        )}

        {/* ── Filters bar ── */}
        <Flex justify="space-between" align="center" mb={5} gap={3} flexWrap="wrap">
          <HStack spacing={3} flexWrap="wrap">
            <InputGroup size="sm" maxW="220px">
              <InputLeftElement><Icon as={FiSearch} color="gray.400" boxSize={3.5} /></InputLeftElement>
              <Input pl={8} placeholder="Search codes, names, notes…" value={query} onChange={e => setQuery(e.target.value)}
                bg="white" borderRadius="lg" />
            </InputGroup>
            <Select size="sm" maxW="160px" value={filterCat} onChange={e => setFilterCat(e.target.value)}
              bg="white" borderRadius="lg">
              <option value="">All categories</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </Select>
          </HStack>

          <HStack spacing={4}>
            <Tooltip label="Show only codes referenced in Products & Services or invoice lines">
              <HStack spacing={1.5} cursor="pointer" onClick={() => setFilterProducts(p => !p)}>
                <Switch size="sm" isChecked={filterProducts} colorScheme="green" onChange={() => {}} />
                <Icon as={FiPackage} color={filterProducts ? 'green.600' : 'gray.400'} boxSize={3.5} />
                <Text fontSize="12px" color={filterProducts ? 'green.700' : 'gray.500'} fontWeight={filterProducts ? '600' : '400'}>In Products</Text>
              </HStack>
            </Tooltip>
            <Tooltip label={`${obsoleteCount} obsolete code${obsoleteCount !== 1 ? 's' : ''}`}>
              <HStack spacing={1.5} cursor="pointer" onClick={() => setShowObsolete(p => !p)}>
                <Switch size="sm" isChecked={showObsolete} colorScheme="orange" onChange={() => {}} />
                <Icon as={FiAlertTriangle} color={showObsolete ? 'orange.500' : 'gray.400'} boxSize={3.5} />
                <Text fontSize="12px" color={showObsolete ? 'orange.600' : 'gray.500'} fontWeight={showObsolete ? '600' : '400'}>
                  Show Obsolete {obsoleteCount > 0 && !showObsolete && <Text as="span" color="orange.400">({obsoleteCount})</Text>}
                </Text>
              </HStack>
            </Tooltip>
            <Tooltip label={`${deletedCount} deleted code${deletedCount !== 1 ? 's' : ''}`}>
              <HStack spacing={1.5} cursor="pointer" onClick={() => setShowDeleted(p => !p)}>
                <Switch size="sm" isChecked={showDeleted} colorScheme="red" onChange={() => {}} />
                <Icon as={FiTrash2} color={showDeleted ? 'red.500' : 'gray.400'} boxSize={3.5} />
                <Text fontSize="12px" color={showDeleted ? 'red.600' : 'gray.500'} fontWeight={showDeleted ? '600' : '400'}>
                  Show Deleted {deletedCount > 0 && !showDeleted && <Text as="span" color="red.400">({deletedCount})</Text>}
                </Text>
              </HStack>
            </Tooltip>
          </HStack>
        </Flex>

        {/* ── Code list ── */}
        {loading ? (
          <Flex justify="center" py={12}><Spinner size="lg" color="blue.400" /></Flex>
        ) : grouped.length === 0 ? (
          <Box textAlign="center" py={16} color="gray.400">
            <Icon as={FiSearch} boxSize={10} mb={3} opacity={0.3} />
            <Text fontSize="15px" mb={1}>No nominal codes found</Text>
            <Text fontSize="12px">Add codes manually or import from Sage CSV / Excel</Text>
          </Box>
        ) : (
          <VStack spacing={6} align="stretch">
            {grouped.map(group => {
              const net = group.totalDebit - group.totalCredit;
              return (
                <Box key={group.value} borderRadius="xl" overflow="hidden" border="1px solid" borderColor="gray.200" boxShadow="sm">
                  {/* Category header */}
                  <Flex align="center" justify="space-between" px={4} py={2.5} bg="gray.50"
                    borderBottom="1px solid" borderColor="gray.200">
                    <HStack spacing={2}>
                      <Badge colorScheme={group.color} px={2} py={0.5} borderRadius="md" fontSize="11px" fontWeight="700">
                        {group.label}
                      </Badge>
                      <Text fontSize="11px" color="gray.500">{group.items.length} code{group.items.length !== 1 ? 's' : ''}</Text>
                    </HStack>
                    {(group.totalDebit !== 0 || group.totalCredit !== 0) && (
                      <HStack spacing={4} fontSize="11px">
                        {group.totalDebit  !== 0 && <HStack spacing={1}><Icon as={FiTrendingUp}   boxSize={3} color="blue.500" /><Text fontWeight="700" color="blue.700">{fmtBalance(group.totalDebit)}</Text></HStack>}
                        {group.totalCredit !== 0 && <HStack spacing={1}><Icon as={FiTrendingDown} boxSize={3} color="red.500"  /><Text fontWeight="700" color="red.700">{fmtBalance(group.totalCredit)}</Text></HStack>}
                        <Divider orientation="vertical" h="14px" />
                        <Tooltip label="Net balance (Debit − Credit)">
                          <Text fontWeight="800" color={net >= 0 ? 'green.700' : 'red.700'}>
                            Net {net < 0 ? '-' : ''}{fmtBalance(Math.abs(net))}
                          </Text>
                        </Tooltip>
                      </HStack>
                    )}
                  </Flex>

                  <Table size="sm" bg="white">
                    <Thead>
                      <Tr bg="gray.50" borderBottom="1px solid" borderColor="gray.100">
                        <Th fontSize="10px" w="90px" color="gray.500" letterSpacing="wide">Code</Th>
                        <Th fontSize="10px" color="gray.500" letterSpacing="wide">Name / Description</Th>
                        <Th fontSize="10px" w="105px" textAlign="right" color="blue.600" letterSpacing="wide">Debit</Th>
                        <Th fontSize="10px" w="105px" textAlign="right" color="red.600" letterSpacing="wide">Credit</Th>
                        <Th fontSize="10px" w="105px" textAlign="right" color="gray.500" letterSpacing="wide">Net</Th>
                        <Th fontSize="10px" w="130px" color="gray.500" letterSpacing="wide">Replacement</Th>
                        <Th fontSize="10px" w="60px" textAlign="center" color="gray.500" letterSpacing="wide">Used</Th>
                        <Th fontSize="10px" w="75px" textAlign="center" color="gray.500" letterSpacing="wide">Status</Th>
                        <Th fontSize="10px" w="95px" color="gray.500" letterSpacing="wide">Created</Th>
                        <Th w="80px" />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {group.items.map((nc, idx) => {
                        const dr    = parseFloat(nc.debit_balance  || 0);
                        const cr    = parseFloat(nc.credit_balance || 0);
                        const net   = dr - cr;
                        const isObs = !!nc.is_obsolete;
                        const isDel = !!nc.deleted_at;
                        return (
                          <Tr key={nc.id}
                            bg={isDel ? 'red.50' : isObs ? 'orange.50' : idx % 2 === 0 ? 'white' : 'gray.50'}
                            opacity={isDel || isObs ? 0.75 : 1}
                            _hover={{ bg: isDel ? 'red.100' : isObs ? 'orange.100' : 'blue.50' }}
                            transition="background 0.1s">
                            <Td py={2.5}>
                              <Badge colorScheme={isDel ? 'red' : isObs ? 'orange' : catColor(nc.category)}
                                fontSize="11px" fontFamily="mono" fontWeight="700" px={2} py={0.5}>
                                {nc.code}
                              </Badge>
                            </Td>
                            <Td py={2.5}>
                              <Text fontSize="13px" fontWeight="500"
                                textDecoration={isDel || isObs ? 'line-through' : 'none'}
                                color={isDel ? 'red.500' : isObs ? 'orange.700' : 'gray.800'}>
                                {nc.name}
                              </Text>
                              {nc.description && <Text fontSize="10px" color="gray.400" mt={0.5}>{nc.description}</Text>}
                              {nc.notes && (
                                <Tooltip label={nc.notes} fontSize="xs" maxW="320px" placement="top">
                                  <Text fontSize="10px" color="blue.500" cursor="default" mt={0.5} noOfLines={1}>
                                    📝 {nc.notes}
                                  </Text>
                                </Tooltip>
                              )}
                            </Td>
                            <Td textAlign="right" py={2.5}>
                              <Text fontSize="12px" fontWeight="600" color={dr > 0 ? 'blue.700' : 'gray.300'}>{dr > 0 ? fmtBalance(dr) : '—'}</Text>
                            </Td>
                            <Td textAlign="right" py={2.5}>
                              <Text fontSize="12px" fontWeight="600" color={cr > 0 ? 'red.700' : 'gray.300'}>{cr > 0 ? fmtBalance(cr) : '—'}</Text>
                            </Td>
                            <Td textAlign="right" py={2.5}>
                              <Text fontSize="12px" fontWeight="700" color={net > 0 ? 'green.700' : net < 0 ? 'red.700' : 'gray.300'}>
                                {net !== 0 ? `${net < 0 ? '-' : ''}${fmtBalance(Math.abs(net))}` : '—'}
                              </Text>
                            </Td>
                            <Td py={2.5}>
                              {nc.replacement_code ? (
                                <Tooltip label={nc.replacement_name} fontSize="xs">
                                  <Tag size="sm" colorScheme="purple" variant="subtle" cursor="default">
                                    <TagLabel fontSize="10px" fontFamily="mono" fontWeight="600">{nc.replacement_code}</TagLabel>
                                  </Tag>
                                </Tooltip>
                              ) : <Text fontSize="11px" color="gray.300">—</Text>}
                            </Td>
                            <Td textAlign="center" py={2.5}>
                              {(nc.product_count > 0 || nc.invoice_item_count > 0) ? (
                                <Tooltip label={`${nc.product_count || 0} product(s) · ${nc.invoice_item_count || 0} invoice line(s)`}>
                                  <Badge colorScheme="green" fontSize="9px" cursor="default">
                                    {(nc.product_count || 0) + (nc.invoice_item_count || 0)}
                                  </Badge>
                                </Tooltip>
                              ) : <Text fontSize="11px" color="gray.300">—</Text>}
                            </Td>
                            <Td textAlign="center" py={2.5}>
                              {isDel ? <Badge colorScheme="red"    fontSize="9px" px={2}>Deleted</Badge>
                              : isObs ? <Badge colorScheme="orange" fontSize="9px" px={2}>Obsolete</Badge>
                              :         <Badge colorScheme="green"  fontSize="9px" px={2}>Active</Badge>}
                            </Td>
                            <Td py={2.5}>
                              <Text fontSize="10px" color="gray.500">{fmtDate(nc.created_at)}</Text>
                              {nc.created_by_name && <Text fontSize="9px" color="gray.400">{nc.created_by_name}</Text>}
                              {isDel && (
                                <Tooltip label={`Deleted by ${nc.deleted_by_name || 'unknown'}`}>
                                  <Text fontSize="9px" color="red.400">{fmtDate(nc.deleted_at)}</Text>
                                </Tooltip>
                              )}
                            </Td>
                            <Td py={2.5}>
                              {isDel ? (
                                <Tooltip label="Restore this code">
                                  <IconButton icon={<FiRotateCcw size={12} />} size="xs" variant="ghost" colorScheme="green"
                                    onClick={() => restoreCode(nc.id)} aria-label="Restore" />
                                </Tooltip>
                              ) : (
                                <HStack spacing={0}>
                                  <Tooltip label={isObs ? 'Mark as Active' : 'Mark as Obsolete'}>
                                    <IconButton icon={<FiAlertTriangle size={12} />} size="xs" variant="ghost"
                                      colorScheme={isObs ? 'green' : 'orange'}
                                      onClick={() => toggleObsolete(nc)} aria-label={isObs ? 'Activate' : 'Obsolete'} />
                                  </Tooltip>
                                  <IconButton icon={<FiEdit2 size={12} />} size="xs" variant="ghost" colorScheme="blue"
                                    onClick={() => openEdit(nc)} aria-label="Edit" />
                                  <IconButton icon={<FiTrash2 size={12} />} size="xs" variant="ghost" colorScheme="red"
                                    onClick={() => deleteCode(nc.id)} aria-label="Delete" />
                                </HStack>
                              )}
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              );
            })}
          </VStack>
        )}
      </Box>
    </PageShell>
  );
}
